import AlgoliaProvider from "@components/algolia/AlgoliaProvider";
import { getSiteSettings } from "@config/site/site-api-settings";
import { getPageContentBySlug } from "@contentful-api/queries/pagePath/pagePathService";
import { getSiteSettingsContent } from "@contentful-api/queries/siteSettings/siteSettingsService";
import type { IPageProductListingPage } from "@contentful-api/types/contentful";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { MicrocopyProvider } from "@lib/contentful/microcopy/MicrocopyContext";
import { PageTypes } from "@lib/enums/PageTypes";
import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import { getProductListLayout } from "@lib/utils/layoutUtility";
import { getAdditionalSizesFilters } from "@lib/utils/plp/plpShoeSizes";
import { getInitialFiltersList } from "@lib/utils/productListingPageUtils";
import { getAlgoliaIndexName } from "@lib/wrappers/algolia";
import { useStore, ZustandProvider } from "@store/index";
import { serialize } from "@ungap/structured-clone";
import { isEqual } from "lodash-es";
import type { GetServerSidePropsContext, PreviewData } from "next";
import { useRouter } from "next/router";
import type { ParsedUrlQuery } from "querystring";
import { useEffect } from "react";
import { renderToString } from "react-dom/server";
import { getServerState, InstantSearchSSRProvider } from "react-instantsearch";
import { SearchPageTemplate } from "templates/SearchPage";
import type { NextPageWithLayout } from "../interfaces/PageInterfaces";

export type SearchProps = Awaited<ReturnType<typeof getServerSideProps>>["props"];

export const SearchPage: NextPageWithLayout<SearchProps> = ({
    serializedSiteSettings,
    serializedPage,
    initialFiltersList,
    instantSearchServerState,
    ...props
}) => {
    const initialFilters = useStore((state) => state.data.products.initialFilters);
    const setInitialFilters = useStore((state) => state.setInitialFilters);

    const { query, locale } = useRouter();
    useEffect(() => {
        if (!isEqual(initialFilters, props.initialZustandState?.products?.initialFilters)) {
            setInitialFilters(props.initialZustandState?.products?.initialFilters);
        }
    }, [query]);

    return (
        <InstantSearchSSRProvider {...instantSearchServerState}>
            <AlgoliaProvider locale={locale}>
                <SearchPageTemplate
                    serializedSiteSettings={serializedSiteSettings}
                    serializedPage={serializedPage}
                    initialFiltersList={initialFiltersList}
                    query={query}
                    locale={locale}
                />
            </AlgoliaProvider>
        </InstantSearchSSRProvider>
    );
};

export default SearchPage;

SearchPage.getLayout = getProductListLayout;

export const getServerSideProps = async ({
    locale,
    query,
    preview,
}: GetServerSidePropsContext<ParsedUrlQuery, PreviewData>) => {
    const startTime = performance.now();

    const [siteSettings, pagePath] = await Promise.all([
        getSiteSettingsContent({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.SEARCH,
            selectedFields: ["fields.sizeGuideModal"],
            microcopySetKey: "plp",
            isPreview: preview,
        }),
        getPageContentBySlug({
            locale,
            localeTagId: getSiteSettings.contentfulLocaleTagId,
            pageType: PageTypes.SEARCH,
            basePath: searchPage,
            isPreview: preview,
        }),
    ]);

    if (!pagePath) {
        return {
            notFound: true,
        };
    }

    const { content, sharedMicrocopySet, pageMicrocopySet } = siteSettings ?? {};
    const pageData = pagePath.fields.page as IPageProductListingPage;

    // Serialize Contentful data to remove circular references and ensure it's JSON-safe for Next.js
    const serializedSiteSettings = serialize(content);
    const serializedSharedMicrocopySet = serialize(sharedMicrocopySet);
    const serializedPageMicrocopySet = serialize(pageMicrocopySet);
    const serializedSizeGuideModal = serialize(content.fields.sizeGuideModal);
    const serializedPage = serialize(pageData);
    const pathName = getPagePathByLocale(searchPage, locale);

    const algoliaStartTime = performance.now();
    const instantSearchServerState = await getServerState(
        <ZustandProvider initialState={{ products: { initialFilters: {} } }}>
            <MicrocopyProvider
                value={{
                    shared: serializedSharedMicrocopySet,
                    page: serializedPageMicrocopySet,
                }}
            >
                <AlgoliaProvider locale={locale}>
                    <SearchPageTemplate
                        serializedSiteSettings={serializedSiteSettings}
                        serializedPage={serializedPage}
                        initialFiltersList={{}}
                        query={query}
                        locale={locale}
                    />
                </AlgoliaProvider>
            </MicrocopyProvider>
        </ZustandProvider>,
        {
            renderToString,
        }
    );
    const mappedFilters = {};
    const filtersList = [
        ...(pageData?.fields?.filtersList ?? []),
        ...getAdditionalSizesFilters(pageData?.fields?.filtersList),
    ];
    const initialFiltersList = getInitialFiltersList(
        instantSearchServerState,
        filtersList.map((it) => it.fields.facetName)
    );
    filtersList.forEach((filter) => {
        mappedFilters[filter?.fields?.filterName] = initialFiltersList[
            filter?.fields?.facetName
        ]?.map((it) => {
            return { value: it, label: it };
        });
    });
    Logger.info(ServiceType.ALGOLIA, "SearchPage Algolia Query", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - algoliaStartTime,
        pageType: PageTypes.SEARCH,
    });

    const redirectUrl =
        instantSearchServerState?.initialResults?.[getAlgoliaIndexName(locale)]?.results[0]
            ?.renderingContent?.redirect?.url;
    if (redirectUrl) {
        let path;
        try {
            path = new URL(redirectUrl).pathname;
        } catch {
            path = redirectUrl.startsWith("/") ? redirectUrl : `/${redirectUrl}`;
        }

        return {
            redirect: {
                destination: path,
                permanent: false,
            },
        };
    }

    Logger.info(ServiceType.NEXT_API, "SearchPage Total Time", {
        tag: LogTag.PERFORMANCE,
        duration: performance.now() - startTime,
        pageType: PageTypes.SEARCH,
    });

    return {
        props: {
            serializedSiteSettings,
            serializedSharedMicrocopySet,
            serializedPageMicrocopySet,
            serializedSizeGuideModal,
            serializedPage,
            PageType: PageTypes.SEARCH,
            pathName,
            initialFiltersList,
            instantSearchServerState,
            initialZustandState: {
                products: {
                    list: [],
                    currentFilters: [],
                    resultsCount: null,
                    lastVisitedPlp: null,
                    algoliaQueryID: null,
                    initialFilters: mappedFilters,
                },
            },
            query,
            locale,
        },
    };
};
